import { isCommissionProduct } from '@weft/services';
import type { CommissionProductVariantInput, ProductInput, PromotionInput } from '@weft/types';
import { BILLING_INTERVALS, PROMOTION_TYPES } from '@weft/types';
import { raise, raiseUnreachable, sumBy, withDefault } from '@weft/utils';
import { percentageRatio } from '@weft/utils';
import { useFormContext } from 'react-hook-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { getProductInputEstimation } from '../utils/live-preview-estimation';

export function usePlanTotalAmount() {
  const { watch } = useFormContext<PlanInputForm>();

  const billingInterval = withDefault(watch('billingData.billingInterval'), BILLING_INTERVALS.Monthly);
  const fixedProducts = withDefault(watch('fixedProducts'), []);
  const usageProducts = withDefault(watch('usageProducts'), []);
  const products = [...fixedProducts, ...usageProducts];
  const promotions = withDefault(watch('promotions'), []);

  const commissionProductsVariants = products.filter(product => isCommissionProduct(product as ProductInput)).map(product => product.variants[0]);

  const productsValueAmount = sumBy(products, (product) => getProductInputEstimation(product as ProductInput, billingInterval));
  const promotionsValueAmount = promotions.reduce((acc, promotion) => acc + calculatePromotion(promotion, productsValueAmount), 0);

  return calculateCommission(productsValueAmount, promotionsValueAmount, commissionProductsVariants as CommissionProductVariantInput[]);
}

function calculatePromotion(promotion: PromotionInput, productsAmount: number) {
  switch (promotion.promotionModel.promotionType) {
    case PROMOTION_TYPES.Percentage: {
      const percentage = withDefault(promotion.promotionModel.promotionConfiguration?.percentage, 0);

      return percentage === 0 ? 0 : productsAmount * percentageRatio(percentage);
    }
    case PROMOTION_TYPES.Credit:
      return withDefault(promotion.promotionModel.promotionConfiguration.creditAmount, 0);
    case PROMOTION_TYPES.FreeTrial:
      return raise('Not implemented');
    default:
      return raiseUnreachable(promotion.promotionModel);
  }
}

function calculateCommission(productsValueAmount: number, promotionsValueAmount: number, commissionProductsVariants: CommissionProductVariantInput[]) {
  const totalAmountBeforeCommission = productsValueAmount - promotionsValueAmount;

  return commissionProductsVariants.reduce((accumulatedSum, commissionProductVariant) => {
    const commissionPercentage = withDefault(commissionProductVariant.pricing.priceConfiguration?.percentage, 0);
    const commission = commissionPercentage > 0 ? percentageRatio(commissionPercentage) * accumulatedSum : 0;

    return accumulatedSum + commission;
  }, totalAmountBeforeCommission);
}